import {
  Box,
  Container,
  Grid,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { getDeliveryWindow, getOrderDate } from "../utils";

import { ITheme } from "../constants/theme";
import { Stamp } from "../components-atoms";
import moment from "moment";
import { useSelector } from "react-redux";
import { textButtonHoverAndActive } from "../utils/styles/buttonStyles";
import { getDeliveryDate } from "../utils/getDeliveryWindow";
import { getLastUpdateDate } from "../utils/getOrderDate";
import { IOrderListItem } from "../utils/deliveryUtil";
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme: ITheme) => ({
  container: {
    marginBottom: "24px",
  },
  root: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
    boxSizing: "border-box",
    cursor: "pointer",
    transition: "all 200ms ease",
    "@media (min-width: 900px)": {
      border: "1px solid rgba(0, 0, 0, 0.08)",
      borderRadius: "20px",
    },
    ...textButtonHoverAndActive
  },
  gridMargin: {
    marginTop: 8,
  },
  rightContainer: {
    display: "flex",
    flexDirection: "column",
    alignSelf: "flex-end",
    textAlign: "right",
    minWidth: 220,
    "@media (max-width: 899px)": {
      marginLeft: 0,
      alignItems: "flex-start",
    },
  },
  leftContainer: {
    display: "flex",
  },
  topContainer: {
    alignItems: "flex-start",
  },
  stamp: {
    "@media (max-width: 599px)": {
      alignSelf: "flex-start",
      marginTop: 8,
      marginBottom: 8,
      marginLeft: 0,
    },
  },
}));

export enum IOrderItemSource {
  "Open" = "Open",
  "ReadyForCollection" = "ReadyForCollection",
  "Failed" = "Failed",
  "Archived" = "Archived",
}
interface IProps {
  data: IOrderListItem;
  onClick: (item: IOrderListItem) => void;
  source?: IOrderItemSource;
}
export function OrderItem({ data, onClick, source }: IProps) {
  const classes = useStyles();
  const { t } = useTranslation();
  const role = useSelector((state: any) => state.login.role);
  const isTKAdmin = role === "Admin";
  const isMerchant = role === "Merchant";

  const previousTimeStamp = useSelector(
    (state: any) => state.checkNewOrdersPending.previousTimeStamp
  );
  const checkNewItem = () => {
    if (!previousTimeStamp) {
      return false;
    }
    const previousCheckNewOrderTime = moment(
      previousTimeStamp,
      "YYYYMMDD HH:mm:ss a"
    );
    const orderTime = moment(data.Pending, "YYYYMMDD HH:mm:ss a");
    return previousCheckNewOrderTime <= orderTime;
  };

  const renderInfo = () => {
    switch (source) {
      case IOrderItemSource.Failed:
        return (
          <Grid item xs={12} sm className={classes.rightContainer}>
            <Typography variant="body2" component="span" color="textSecondary">
              {t('orderItem.failedOn')}
            </Typography>
            <Typography
              variant="subtitle2"
              component="span"
              color="textPrimary"
              className={classes.gridMargin}
            >
              {getDeliveryDate(data.Failed)}
            </Typography>
          </Grid>
        )
      case IOrderItemSource.Archived:
        return (
          <Grid item xs={12} sm className={classes.rightContainer}>
            <Typography variant="body2" component="span" color="textSecondary">
              {t('orderItem.lastUpdated')}
            </Typography>
            <Typography
              variant="subtitle2"
              component="span"
              color="textPrimary"
              className={classes.gridMargin}
            >
              {getLastUpdateDate(data)}
            </Typography>
          </Grid>
        )
      default:
        return (
          <Grid item xs={12} sm className={classes.rightContainer}>
            <Typography variant="body2" component="span" color="textSecondary">
              {t('orderItem.deliveryWindow')}
            </Typography>
            <Typography
              variant="subtitle2"
              component="span"
              color="textPrimary"
              className={classes.gridMargin}
            >
              {getDeliveryWindow(data.TimeSlotFrom, data.TimeSlotTo)}
            </Typography>
          </Grid>
        )
    }
  }

  return (
    <Box
      data-order="order-item"
      onClick={() => onClick(data)}
      className={classes.container}
    >
      <Container
        className={classes.root}
        style={{
          backgroundColor: checkNewItem() ? "#B302833D" : undefined,
          borderColor: checkNewItem() ? "#B3028352" : undefined,
        }}
      >
        <Grid container>
          <Grid xs={12} sm container item className={classes.topContainer}>
            <Grid item xs={12} className={classes.leftContainer}>
              <Typography
                variant="h6"
                component="span"
                color="textPrimary"
                display="inline"
              >
                {data.OrderNo}
              </Typography>
              {data.Cancelled !== null ? (
                <Stamp type="fail" text={t('orderDetails.cancelledOrder')} />
              ) : (
                <></>
              )}
              {(data.Status === "AuthorizationRequired" || data.ProOrderStatus === "Authorization Required") ? (
                <Stamp type="fail" text={t('orderDetails.authorisationRequired')} />
              ) : (
                <></>
              )}
              {data.Status === "RejectedDelivery" ? (
                <Stamp type="fail" text={t('orderDetails.rejectedOrder')} />
              ) : (
                <></>
              )}
              {data.Status === "RejectedByManager" ? (
                <Stamp type="fail" text={t('orderDetails.rejectedByManager')} />
              ) : (
                <></>
              )}
              {data.Status === "PendingApproval" ? (
                <Stamp type="warning" text={t('orderDetails.pendingApproval')} />
              ) : (
                <></>
              )}
              {!data.Delivered &&
                !data.Failed &&
                data.OutForDelivery !== null ? (
                <Stamp type="warning" text={t('orderDetails.outForDelivery')} />
              ) : (
                <></>
              )}
              {data.Delivered !== null ? (
                <Stamp type="success" text={t('orderDetails.delivered')} />
              ) : (
                <></>
              )}
              {!!data.Failed && source !== IOrderItemSource.Archived && (
                <Stamp type="fail" text={t('orderDetails.failedDelivery')} />
              )}
              {data.ManuallyRefunded !== null &&
                data.ShopPortalStatus === "Archived" ? (
                <Stamp type="refunded" text={t('orderDetails.refunded')} />
              ) : (
                <></>
              )}
            </Grid>
            <Grid item xs={12} container className={classes.gridMargin}>
              <Typography
                variant="body2"
                component="span"
                color="textSecondary"
                style={{ textAlign: "left" }}
              >
                {getOrderDate(data.Created)}
                {isTKAdmin || isMerchant ? <strong> · {data?.ShopName}</strong> : null}
              </Typography>
            </Grid>
          </Grid>
          {renderInfo()}
        </Grid>
      </Container>
    </Box>
  );
}
